import { useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  rectIntersection,
} from '@dnd-kit/core';
import {
  arrayMove,
} from '@dnd-kit/sortable';
import { Backdrop, CircularProgress, Grid, Typography } from '@mui/material';
import { connect } from 'react-redux';
import Column from './Column';
import { SortableItem } from './SortableItem';
import { getallNotisForAufgabe, assignNotifications } from '../../actions/notification';
import { useScreenSize } from '../../components/Atoms/ScreenSizeHook';
import { socket } from '../../App';

export default connect(
  ({
    data,
    subscribedEquipmentpools,
    subscribedEquipments,
    subscribedAreas,
    subscribedSites,
    aufgabeNotifications,
  }) => ({
    data,
    subscribedEquipmentpools,
    subscribedEquipments,
    subscribedAreas,
    subscribedSites,
    aufgabeNotifications,
  }),
  {
    getallNotisForAufgabe,
    assignNotifications,
  }
)((props) => {

  const { _users } = props;

  const { windowWidth, breakpoint } = useScreenSize();

  const { notificationTypesFilter, userFilter, areaPoolFilter, showEquipment, isFocused } = props;

  const [backendOutputObj, setbackendOutputObj] = useState({});

  const [columns, setColumns] = useState({
    unassignedNotification: [],
  });

  const [activeId, setActiveId] = useState(null);
  const [expanded, setExpanded] = useState({});         // handeling collapse and expand

  const [taskMoving, setTaskMoving] = useState(false);
  const isFetchingRef = useRef(false);
  const refreshTimeoutRef = useRef(null);

  const activeColumnRef = useRef(null);

  const sensors = useSensors(useSensor(PointerSensor, {
    activationConstraint: {
      distance: 5
    }
  }));    // builtin by library


  // finding column (board)

  const findContainer = (id) => {
    return Object.keys(columns).find((key) => columns[key].find((key) => { return key.nodeid === id }));
  };

  const handleDragStart = (event) => {
    const { active } = event;
    const colId = findContainer(active.id);
    activeColumnRef.current = colId ?? null;
    setActiveId(active.id);
  };

  const makePayloadForBackend = (source, destination, notificationnodeid, parentnodeid) => {

    let _tempbackendObj = {};

    if (destination === "unassignedNotification") {
      //in case of move notificaiton from user box to unassigned box

      _tempbackendObj = {
        "usernodeid": source,
        "unassignednotification": notificationnodeid
      }
    }

    else {
      _tempbackendObj = {
        "usernodeid": destination,
        "assignednotification": {
          "notificationnodeid": notificationnodeid,
          "parentnodeid": parentnodeid
        }
      }
    }

    setbackendOutputObj(_tempbackendObj);
  }


  const handleDragEnd = (event) => {

    // study dndnext kit docs for better overview

    let _tempColmns = {};

    const { active, over } = event;
    setActiveId(null);
    if (!over || active.id === over.id) return;

    const sourceCol = findContainer(active.id);
    const destCol = columns[over.id] ? over.id : findContainer(over.id);

    if (!sourceCol || !destCol) return;

    const sourceItems = [...columns[sourceCol]];
    const destItems = [...columns[destCol]];

    let parentNodeId = "";

    const activeIndex = sourceItems.findIndex((item) => item.nodeid === active.id);
    const draggedItem = sourceItems[activeIndex];

    const overIndex = destItems.findIndex((item) => item.nodeid === over.id);

    if (sourceCol === destCol) {

      // drag and drop in case column (board)

      const newItems = arrayMove(sourceItems, activeIndex, overIndex);
      _tempColmns = { ...columns, [sourceCol]: newItems }
      setColumns(_tempColmns);
    } else {

      parentNodeId = sourceCol === "unassignedNotification" ? "" : sourceCol;

      // console.log('Moved from:', sourceCol, 'to:', destCol, "item : ", active.id);

      sourceItems.splice(activeIndex, 1);

      const updatedDraggedItem = { ...draggedItem, parentNodeid: parentNodeId };

      destItems.splice(overIndex >= 0 ? overIndex : destItems?.length, 0, updatedDraggedItem);

      _tempColmns = {
        ...columns,
        [sourceCol]: sourceItems,
        [destCol]: destItems,
      }

      makePayloadForBackend(sourceCol, destCol, active.id, parentNodeId);
      setColumns(_tempColmns);
    }
  };

  // expand or collapse view
  const toggleAccordion = (colId, isExpanded) => {
    setExpanded((prev) => ({ ...prev, [colId]: isExpanded }));
  };


  // find notification for overlay ( you see when you drag start (shadow))

  let targetNotification = undefined;

  Object.keys(columns).find((key) => columns[key].find((key) => {
    if (key.nodeid === activeId) {
      targetNotification = key
    }
  }));

  // appending users in initialData
  const toEpoch = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === "number") {
      return value < 1000000000000 ? value * 1000 : value;
    }
    if (typeof value === "string") {
      const numeric = Number(value);
      if (!Number.isNaN(numeric)) {
        return numeric < 1000000000000 ? numeric * 1000 : numeric;
      }
      const parsed = Date.parse(value);
      return Number.isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  };

  const getNotificationTimestamp = (notification) => {
    const candidates = [
      notification?.created,
      notification?.updated,
      notification?.modified,
      notification?.timestamp,
      notification?.progressstarted,
    ];
    return Math.max(...candidates.map(toEpoch), 0);
  };

  const sortByLatestFirst = (items = []) =>
    [...items].sort((a, b) => getNotificationTimestamp(b) - getNotificationTimestamp(a));

  const appendSocketNotificationToBoard = (notification) => {
    if (!notification?.nodeid) return;
    setColumns((prev) => {
      const hasAlready = Object.values(prev || {}).some((list) =>
        Array.isArray(list) && list.some((item) => item?.nodeid === notification.nodeid)
      );
      if (hasAlready) return prev;

      const next = {
        ...(prev || {}),
        unassignedNotification: [
          { ...notification },
          ...((prev?.unassignedNotification || []).map((item) => ({ ...item }))),
        ],
      };
      next.unassignedNotification = sortByLatestFirst(next.unassignedNotification);
      return next;
    });
  };

  const transformNotifications = (inputJson, userList) => {
    // Step 1: Initialize result object with all users
    const result = {
      unassignedNotification: {},
    };

    userList?.length && userList.forEach((usr) => {
      const userNodeId = usr?.user?.nodeid;
      if (userNodeId) {
        result[userNodeId] = [];
      }
    });

    result.unassignedNotification = [];

    // Step 2: Process equipment data
    const data = inputJson?.[0]?.data || [];

    data.forEach((item) => {
      const equipment = item.equipment;
      const equipmentNodeId = equipment?.nodeid;

      if (equipment.hasnotification) {
        equipment.hasnotification.forEach((n) => {
          const notification = { ...n.notification, equipmentnodeid: equipmentNodeId };

          // If notification has assigned users
          if (notification.isassignedto && Array.isArray(notification.isassignedto)) {
            let assigned = false;
            notification.isassignedto.forEach((userWrap) => {
              const userId = userWrap.user?.nodeid;
              if (userId && result.hasOwnProperty(userId)) {
                result[userId].push(notification);
                assigned = true;
              }
            });

            // Fallback if assigned to unknown user
            if (!assigned) {
              result.unassignedNotification.push(notification);
            }
          } else {
            // No assignment at all
            result.unassignedNotification.push(notification);
          }
        });
      }
    });

    Object.keys(result).forEach((key) => {
      result[key] = sortByLatestFirst(result[key]);
    });

    return result;
  };

  const getAllNotis = () => {
    if (isFetchingRef.current) return;
    let nodeIds = [];
    for (const [key, value] of Object.entries(props.subscribedEquipments)) {
      nodeIds.push(value.nodeid);
    }
    isFetchingRef.current = true;
    props.getallNotisForAufgabe({ equipmentnodeids: nodeIds, lang: "de", status: "2" }, "AUFGABE_NOTIS")
  }

  const scheduleRefresh = (delay = 250) => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    refreshTimeoutRef.current = setTimeout(() => {
      getAllNotis();
    }, delay);
  };

  // getting data for initial render
  useEffect(() => {
    getAllNotis();
  }, [isFocused]);


  // making data for UI state
  useEffect(() => {
    if (props.aufgabeNotifications?.length) {
      isFetchingRef.current = false;
      if (props.aufgabeNotifications[0].data?.length) {
        const structuredData = transformNotifications(props.aufgabeNotifications, _users);
        setColumns(structuredData); // or setState(structuredData)
      }
    }
  }, [props.aufgabeNotifications])


  // whenever if there is any assignment process ( onDropEnd ) , calls backend API
  useEffect(() => {
    if (Object.keys(backendOutputObj)?.length) {
      props.assignNotifications(backendOutputObj, "ASSIGN_NOTIFICATION");
    }
  }, [backendOutputObj]);

  useEffect(() => {
    const onCreated = (payload) => {
      const socketNotification = payload?.notification;
      if (socketNotification) {
        appendSocketNotificationToBoard(socketNotification);
      }
      scheduleRefresh(150);
    };
    const onEqCreated = (payload) => {
      const socketNotification = payload?.notification;
      if (socketNotification) {
        appendSocketNotificationToBoard(socketNotification);
      }
      scheduleRefresh(150);
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        scheduleRefresh(100);
      }
    };
    const onFocus = () => scheduleRefresh(100);

    socket.on("server:notification:created", onCreated);
    socket.on("server:eqNotification:created", onEqCreated);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);

    const pollId = setInterval(() => {
      scheduleRefresh(0);
    }, 30000);

    return () => {
      socket.off("server:notification:created", onCreated);
      socket.off("server:eqNotification:created", onEqCreated);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
      clearInterval(pollId);
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [props.subscribedEquipments]);

  const parseAreaPoolValue = (values) => {
    if (Array.isArray(values) && values.length > 0) {
      return values.map(val => {
        const [type, nodeid] = val.split("==>");
        return { type, nodeid };
      });
    }
    return []; // Return empty array if nothing is selected
  };

  // this function is used to get Notification based on UI filters
  const filterTransformedNotifications = (data, selectedTypes, selectedUsers, areaPoolFilterValues, _dataLookup) => {
    const result = {};

    // 1. Parse the array of filters
    const selectedLocations = parseAreaPoolValue(areaPoolFilterValues);

    const matchesType = (notification) =>
      selectedTypes.includes('all') || selectedTypes.includes(notification.type);

    const matchesUser = (key) =>
      selectedUsers.includes('all') || selectedUsers.includes(key);

    const matchesLocation = (notification) => {
      // If no filters selected or "All Pools" is explicitly selected
      if (!selectedLocations.length || areaPoolFilterValues.includes('p==>0')) return true;

      const equipmentId = notification.equipmentnodeid;
      const info = _dataLookup[equipmentId];
      if (!info) return false;

      // Check if the equipment matches ANY of the selected locations in the array
      return selectedLocations.some(loc => {
        if (loc.type === 's') return info.site?.nodeid === loc.nodeid;
        if (loc.type === 'a') return info.area?.nodeid === loc.nodeid;
        if (loc.type === 'p') return info.equipmentpool?.nodeid === loc.nodeid;
        return false;
      });
    };

    Object.entries(data).forEach(([key, notifications]) => {
      if (key === 'unassignedNotification' || matchesUser(key)) {
        result[key] = notifications
          .filter((n) => matchesType(n) && matchesLocation(n))
          .map((n) => ({ ...n }));
      }
    });

    return result;
  };


  // this function is used to check the relation of notification to equipment ( either its from area or pool)
  const buildDataLookup = () => {
    const data = props.aufgabeNotifications?.[0]?.data || [];

    // console.log("📦 rawJson.result[0].data length:", data?.length);
    if (!data || !Array.isArray(data)) {
      // console.error("❌ Invalid input passed to buildDataLookup");
      return {};
    }

    const lookup = {};

    data.forEach(({ equipment }) => {
      const equipmentId = equipment.nodeid;

      const info = {
        site: null,
        area: null,
        equipmentpool: null,
      };

      const links = Array.isArray(equipment.isequipmentof)
        ? equipment.isequipmentof
        : [equipment.isequipmentof];

      links.forEach((link) => {
        if (link.site) {
          info.site = link.site;
        }

        if (link.area) {
          info.area = link.area;
          const siteViaArea = link.area.isareaof?.[0]?.site;
          if (siteViaArea) {
            info.site = siteViaArea;
          }
        }

        if (link.equipmentpool) {
          info.equipmentpool = link.equipmentpool;
        }
      });

      lookup[equipmentId] = info;
    });

    return lookup;
  };


  // Wrap the lookup builder so it only runs when notifications change
  const _dataLookup = useMemo(() => {
    return buildDataLookup();
  }, [props.aufgabeNotifications]);

  const filteredData = useMemo(() => {
    // DELETE THIS LINE: setRefresh(!refresh); 
    return filterTransformedNotifications(
      columns,
      notificationTypesFilter,
      userFilter,
      areaPoolFilter,
      _dataLookup
    );
  }, [columns, notificationTypesFilter, userFilter, areaPoolFilter, _dataLookup]);

  // in case if user choose a notification from Placeholder Menu ( by clicking on zuweisen) OR from the Users List ( Notification Category Badge)
  const handleClickPromAssignment = (notificationNodeid, usernodeid, parentNodeId) => {
    setTaskMoving(true);
    let _tempbackendObj = {};
    _tempbackendObj = {
      "usernodeid": usernodeid,
      "assignednotification": {
        "notificationnodeid": notificationNodeid,
        "parentnodeid": parentNodeId // "" because from unassigned to any user... , 
      }
    }

    setbackendOutputObj(_tempbackendObj);

    // get latest data from backend
    setTimeout(() => {
      setTaskMoving(false);
      getAllNotis();
    }, 1500)
  }

  const handleUserClick = (destination, notificationnodeid, source) => {
    handleClickPromAssignment(notificationnodeid, destination, source);
  }

  let colSize = 12;

  if (windowWidth <= 3900) {
    colSize = isFocused ? 1.5 : 6; // lg

    if (windowWidth <= 3500) {
      colSize = isFocused ? 2 : 6; // lg
    }

    if (windowWidth <= 1920) {
      colSize = isFocused ? 3 : 6; // lg
    }

    if (windowWidth <= 1440) {
      colSize = isFocused ? 4 : 6; // lg
    }
  }
  return (
    <>

      <Backdrop
        open={taskMoving}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.modal + 1,
          backdropFilter: "blur(4px)",
          flexDirection: "column",
          opacity: 0
        }}
      >
        <CircularProgress color="inherit" />
        <Typography sx={{ mt: 2 }} color={"#FFFFFF"}>Bitte warten...</Typography>
      </Backdrop>

      {
        Object.entries(filteredData)?.length ?
          <div className={`dnd_plannar_wrapper ${isFocused ? "dnd_plannar_wrapper_focused" : ""}`}>
            <DndContext
              sensors={sensors}
              collisionDetection={rectIntersection}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem', borderRadius: "8px" }}>
                <div style={{ display: 'flex', gap: '1.5rem' }}>
                  <Column _users={_users} handleUserClick={handleUserClick} isFocused={isFocused} showEquipment={showEquipment} colId="unassignedNotification" items={filteredData.unassignedNotification} expanded={expanded['unassignedNotification']} onToggle={(e, isExp) => toggleAccordion('unassignedNotification', isExp)} activeId={activeId} />
                </div>
                <Grid container spacing={2}>
                  {Object.entries(filteredData)
                    .filter(([key]) => key !== 'unassignedNotification')
                    .map(([colId, items]) => (
                      <Grid item xs={colSize} key={colId}>
                        <Column _users={_users} handleUserClick={handleUserClick} handleClickFromUnassignedMenuList={handleClickPromAssignment} unassignedNotification={filteredData.unassignedNotification} isFocused={isFocused} showEquipment={showEquipment} colId={colId} items={items} expanded={expanded[colId]} onToggle={(e, isExp) => toggleAccordion(colId, isExp)} activeId={activeId} />
                      </Grid>
                    ))}
                </Grid>
              </div>
              <DragOverlay>
                {activeId ? <SortableItem id={activeId} item={targetNotification} showEquipment={showEquipment} /> : null}
              </DragOverlay>
            </DndContext>

            <br />
            {/* <Divider orientation='horizontal' />
            <br /> */}

            {/* <Typography component={"h1"}><b>API Request :</b></Typography>
            <pre
              style={{
                background: '#0f172a',
                color: '#f8fafc',
                padding: '1rem',
                borderRadius: '8px',
                overflowX: 'auto',
                fontSize: '0.85rem',
              }}
            >
              {JSON.stringify(backendOutputObj, null, 2)}
            </pre> */}
          </div> : ""
      }
    </>
  );
})
